import { useId } from 'react';
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

import { classes } from '../lib/divers';

interface Enveloppe {
  label: string;
  aide?: string;
  erreur?: string | null;
  /**
   * `decrit` porte les identifiants de l'aide et du message d'erreur : le champ doit le
   * recopier dans son `aria-describedby`, sans quoi un lecteur d'écran annonce le libellé mais
   * jamais la contrainte ni la raison du refus.
   */
  children: (idChamp: string, invalide: boolean, decrit: string | undefined) => ReactNode;
}

/** Enveloppe commune : label associé, aide, message d'erreur, liaison ARIA. */
export function Champ({ label, aide, erreur, children }: Enveloppe): JSX.Element {
  const id = useId();
  const idAide = `${id}-aide`;
  const idErreur = `${id}-erreur`;
  const invalide = Boolean(erreur);

  const decrit =
    [aide ? idAide : null, erreur ? idErreur : null].filter((v): v is string => v !== null).join(' ') ||
    undefined;

  return (
    <div className="gv-champ">
      <label className="gv-champ__label" htmlFor={id}>
        {label}
      </label>
      {children(id, invalide, decrit)}
      {aide ? (
        <span className="gv-champ__aide" id={idAide}>
          {aide}
        </span>
      ) : null}
      {erreur ? (
        <span className="gv-champ__erreur" id={idErreur} role="alert">
          {erreur}
        </span>
      ) : null}
    </div>
  );
}

interface ProprietesSaisie extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  aide?: string;
  erreur?: string | null;
}

export function Saisie({ label, aide, erreur, className, ...reste }: ProprietesSaisie): JSX.Element {
  return (
    <Champ label={label} {...(aide ? { aide } : {})} {...(erreur ? { erreur } : {})}>
      {(id, invalide, decrit) => (
        <input
          id={id}
          className={classes('gv-saisie', invalide && 'gv-saisie--erreur', className)}
          aria-invalid={invalide || undefined}
          aria-describedby={decrit}
          {...reste}
        />
      )}
    </Champ>
  );
}

interface ProprietesZone extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  label: string;
  aide?: string;
  erreur?: string | null;
}

export function ZoneTexte({
  label,
  aide,
  erreur,
  className,
  ...reste
}: ProprietesZone): JSX.Element {
  return (
    <Champ label={label} {...(aide ? { aide } : {})} {...(erreur ? { erreur } : {})}>
      {(id, invalide, decrit) => (
        <textarea
          id={id}
          className={classes('gv-saisie', invalide && 'gv-saisie--erreur', className)}
          aria-invalid={invalide || undefined}
          aria-describedby={decrit}
          {...reste}
        />
      )}
    </Champ>
  );
}

export interface OptionSelecteur {
  valeur: string;
  libelle: string;
}

interface ProprietesSelecteur extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: string;
  aide?: string;
  erreur?: string | null;
  options: readonly OptionSelecteur[];
  /** Libellé de l'option vide (« tous les statuts »). Absent = pas d'option vide. */
  optionVide?: string;
}

export function Selecteur({
  label,
  aide,
  erreur,
  options,
  optionVide,
  className,
  ...reste
}: ProprietesSelecteur): JSX.Element {
  return (
    <Champ label={label} {...(aide ? { aide } : {})} {...(erreur ? { erreur } : {})}>
      {(id, invalide, decrit) => (
        <select
          id={id}
          className={classes('gv-saisie', invalide && 'gv-saisie--erreur', className)}
          aria-invalid={invalide || undefined}
          aria-describedby={decrit}
          {...reste}
        >
          {optionVide !== undefined ? <option value="">{optionVide}</option> : null}
          {options.map((o) => (
            <option key={o.valeur} value={o.valeur}>
              {o.libelle}
            </option>
          ))}
        </select>
      )}
    </Champ>
  );
}

/** Interrupteur binaire, avec son libellé cliquable. */
export function Interrupteur({
  libelle,
  actif,
  onChange,
  disabled = false,
}: {
  libelle: ReactNode;
  actif: boolean;
  onChange: (actif: boolean) => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <label className="gv-interrupteur">
      <input
        type="checkbox"
        role="switch"
        checked={actif}
        disabled={disabled}
        onChange={(e) => {
          onChange(e.currentTarget.checked);
        }}
      />
      <span>{libelle}</span>
    </label>
  );
}

/** Sélecteur segmenté (période, granularité) : peu d'options, toutes visibles. */
export function Segmente<T extends string>({
  libelle,
  valeur,
  options,
  onChange,
}: {
  libelle: string;
  valeur: T;
  options: ReadonlyArray<{ valeur: T; libelle: string }>;
  onChange: (valeur: T) => void;
}): JSX.Element {
  return (
    <div className="gv-segmente" role="group" aria-label={libelle}>
      {options.map((o) => (
        <button
          key={o.valeur}
          type="button"
          aria-pressed={o.valeur === valeur}
          onClick={() => {
            onChange(o.valeur);
          }}
        >
          {o.libelle}
        </button>
      ))}
    </div>
  );
}
